import { FC, useMemo } from 'react';
import { format } from 'date-fns';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';

import { StageEntryDto } from 'src/generated/services/TFinancialApi';
import { RoundCard } from './RoundCard';

const formatDateTime = (dateStr?: string) => {
  if (!dateStr) return null;
  try {
    return format(new Date(dateStr), 'dd-MMM-yyyy hh.mm aa zzz');
  } catch {
    return dateStr;
  }
};

const formatDate = (dateStr?: string) => {
  if (!dateStr) return null;
  try {
    return format(new Date(dateStr), 'dd-MMM-yyyy');
  } catch {
    return dateStr;
  }
};

type ReviewRoundsVariant = 'approved' | 'rejected' | 'default';

const getReviewRoundsVariant = (entries: StageEntryDto[]): ReviewRoundsVariant => {
  const lastEntry = entries[entries.length - 1];
  if (!lastEntry) return 'default';
  const category = (lastEntry.actionCategory || '').toUpperCase();
  if (category === 'VALIDATED') return 'approved';
  if (category === 'REJECTED') return 'rejected';
  return 'default';
};

const ReviewRoundsIcon: FC<{ variant: ReviewRoundsVariant }> = ({ variant }) => {
  if (variant === 'approved') {
    return (
      <div className='w-36 h-36 rounded-8 bg-[#CCEBD5] flex items-center justify-center flex-shrink-0'>
        <CheckCircleIcon sx={{ color: '#00992B', fontSize: 20 }} />
      </div>
    );
  }
  if (variant === 'rejected') {
    return (
      <div className='w-36 h-36 rounded-8 bg-[#FCE4E9] flex items-center justify-center flex-shrink-0'>
        <InfoIcon sx={{ color: '#E72547', fontSize: 20 }} />
      </div>
    );
  }
  return (
    <div className='w-36 h-36 rounded-8 bg-[#F5F6FA] flex items-center justify-center flex-shrink-0'>
      <InfoIcon sx={{ color: '#597282', fontSize: 20 }} />
    </div>
  );
};

interface ReviewRoundsGroupProps {
  entries: StageEntryDto[];
}

export const ReviewRoundsGroup: FC<ReviewRoundsGroupProps> = ({ entries }) => {
  const stageDate = entries[entries.length - 1]?.completedAt || entries[0]?.completedAt;
  const subtitle = [...new Set(entries.map((e) => e.from).filter(Boolean))].join(' - ');
  const iconVariant = getReviewRoundsVariant(entries);

  const roundGroups = useMemo(() => {
    const groups: Record<number, StageEntryDto[]> = {};
    entries.forEach((entry) => {
      const round = entry.roundNumber as number;
      if (!groups[round]) groups[round] = [];
      groups[round].push(entry);
    });
    return groups;
  }, [entries]);

  const roundNumbers = Object.keys(roundGroups)
    .map(Number)
    .sort((a, b) => a - b);

  return (
    <div className='relative'>
      <div className='flex gap-16'>
        <ReviewRoundsIcon variant={iconVariant} />
        <div className='flex-1 min-w-0'>
          <div className='flex items-start justify-between gap-8'>
            <div className='min-w-0'>
              <h3 className='text-14 font-semibold text-neutral-90'>Review Rounds</h3>
              {subtitle && <p className='text-12 text-neutral-50 mt-2'>Ops Admin - Finance Manager</p>}
            </div>
            {stageDate && (
              <div className='text-right flex-shrink-0'>
                <p className='text-12 text-neutral-70 font-medium whitespace-nowrap'>{formatDate(stageDate)}</p>
                <p className='text-12 text-neutral-50 whitespace-nowrap mt-1'>{formatDateTime(stageDate)?.split(' ').slice(1).join(' ')}</p>
              </div>
            )}
          </div>
          <div className='mt-12'>
            {roundNumbers.map((round) => (
              <RoundCard key={`round-${round}`} roundNumber={round} entries={roundGroups[round]} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
